import React, { useState, Fragment } from 'react';
import Localize from 'react-intl-universal';
import { Link } from 'react-router-dom';

import { format, isValid, parse } from 'date-fns';
import { get, omit } from 'lodash-es';

import CheckIcon from '@mui/icons-material/Check';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { alpha } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { ADDRESS_OPTIONS, AddressTypes, EmailTypes, PhoneTypes } from '@common/Constants';
import { BOOKING_PROCESS } from '@common/network';
import { dateInitFormats, formatNonISODateToYearMonthDay } from '@common/Util';
import * as routerConstants from '@navigation/Constants.js';
import { formatDataToAddressDto } from '@pages/EventDetails/Util';
import { postByPathAndData } from '@services/BaseApi';

import { CustomFieldTypes } from './AdditionalInformation/util';
import ComponentHeader from './ComponentHeader';
import { participantsOptions, ParticipantStatus } from './Constants';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  height: '50rem',
  margin: 'auto',
  overflowY: 'scroll',
  overflowX: 'hidden',
  '& .MuiDialog-paper': {
    backgroundColor: `${theme.palette.booking.background}!important`,
    [theme.breakpoints.up('xs')]: { width: '100%', margin: '0.5rem' }
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  color: theme.palette.booking.header,
  fontWeight: '700',
  paddingBottom: '0.3rem'
}));

const StyledTableCell = styled(TableCell)(() => ({ fontSize: '1rem', fontWeight: '700' }));

const StyledButton = styled(Button, { shouldForwardProp: (prop) => prop !== 'lastStep' })(
  ({ theme, lastStep }) => ({
    minWidth: '10rem',
    textTransform: 'none',
    fontWeight: '700',
    fontSize: '14px',
    backgroundColor: theme.palette.action.main,
    color: theme.palette.secondary.contrastText,
    display: lastStep ? 'none' : 'inline',
    width: '100%',
    marginBottom: '2.5rem',
    '&:hover': {
      backgroundColor: alpha(get(theme.palette.primary, 'main'), 0.4),
      boxShadow: 'none'
    }
  })
);

const StyledAlert = styled(Alert)(({ theme }) => ({
  color: theme.palette.booking.confirmationText,
  backgroundColor: theme.palette.booking.confirmation,
  display: 'inline-flex',
  marginBottom: '1rem',
  fontSize: '1rem !important'
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.action.main,
  fontWeight: 'bold',
  padding: '0.5rem',
  textAlign: 'center',

  '&:hover': { color: alpha(get(theme.palette.primary, 'light'), 0.4) },
  [theme.breakpoints.up('lg')]: { marginRight: '2rem' }
}));

export const formatStringDateForRequest = (dateString, reverse = false) => {
  if (!dateString) return null;
  return format(
    parse(
      dateString,
      reverse ? dateInitFormats.serverResponseDateFormat : dateInitFormats.datePickerFormat,
      new Date()
    ),
    reverse ? dateInitFormats.datePickerFormat : dateInitFormats.serverResponseDateFormat
  );
};

const formatBookingPerson = (bookingPerson, isBookingPerson = false) => {
  const temp = {
    firstName: bookingPerson?.firstName || '',
    lastName: bookingPerson?.lastName || '',
    birthDate: formatStringDateForRequest(bookingPerson?.birthDate) || null,
    birthPlace: bookingPerson?.birthPlace || '',
    titleId: parseInt(bookingPerson?.title) || null,
    academicTitleId: parseInt(bookingPerson?.academicTitle) || null,
    genderId: bookingPerson?.gender || null,
    companyName: bookingPerson?.company || '',
    addressDto: {
      street: bookingPerson?.street || '',
      number: bookingPerson?.number || '',
      postalCode: bookingPerson?.postCode || '',
      city: bookingPerson?.city || '',
      regionId: bookingPerson?.region?.id || null,
      countryId: bookingPerson?.country?.id || null,
      typeId: 1
    },
    emailDto: { email: bookingPerson?.email, typeId: 2 },
    phoneDto: { phone: bookingPerson?.phone, typeId: 2 },
    mobileDto: { phone: bookingPerson?.mobile, typeId: 3 }
  };

  return isBookingPerson
    ? temp
    : omit(temp, [
        'customerNumber',
        'industrySector',
        'birthDate',
        'birthPlace',
        'genderId',
        'department',
        'customerReference',
        'function'
      ]);
};

const formatBookingProcess = (
  contactsInfo,
  event,
  participantWithoutBookingPerson,
  selectedSessions,
  checkedConsents,
  isEventFree = false
) => {
  return {
    isBookingPersonParticipant: contactsInfo?.isBookingPerson === participantsOptions.participant,
    eventId: event?.id,
    department: contactsInfo?.generalInformation?.department || '',
    customerReference: contactsInfo?.generalInformation?.reference || '',
    function: contactsInfo?.generalInformation?.functionField || '',
    customerNumber: contactsInfo?.generalInformation?.customerNumber || '',
    industrySectorId: parseInt(contactsInfo?.generalInformation?.industrySector) || null,
    nativeLanguageId: contactsInfo?.generalInformation?.language || null,
    bookingPerson: formatBookingPerson(
      { ...contactsInfo?.generalInformation, ...contactsInfo?.contactInformation },
      true
    ),
    useBookingPersonDataAsCommunication:
      contactsInfo?.communicationAddressRadio === ADDRESS_OPTIONS.noAlternative,
    communicationData:
      contactsInfo?.communicationAddressRadio === ADDRESS_OPTIONS.alternative
        ? formatBookingPerson(contactsInfo?.communicationAddress, false)
        : null,
    useBookingPersonDataAsBilling: isEventFree
      ? false
      : contactsInfo?.billingAddressRadio === ADDRESS_OPTIONS.noAlternative,
    billingData:
      contactsInfo?.billingAddressRadio === ADDRESS_OPTIONS.alternative && !isEventFree
        ? formatBookingPerson(contactsInfo?.billingAddress, false)
        : null,
    statusId: 1,
    isPrepaymentEnabled: false,
    eventParticipants: participantWithoutBookingPerson.map((participant, index) =>
      formatParticipantsForBooking(participant, index, event, selectedSessions)
    ),
    vatno: contactsInfo?.generalInformation?.vatNo,
    eventConsentIds: Object.keys(checkedConsents)
      .filter((key) => checkedConsents[key])
      .map(Number)
  };
};

const formatCustomFieldValueForSave = (values, fieldId, customFields) => {
  if (!values) return null;
  const found = customFields?.find((el) => el?.id === parseInt(fieldId, 10));

  if (found && found.customFieldType?.id === CustomFieldTypes.DATE_TIME && values) {
    try {
      const parsedDateTime = parse(values, dateInitFormats.dateTimePickerFormat, new Date());
      if (isValid(parsedDateTime)) {
        return format(parsedDateTime, dateInitFormats.serverResponseDateTimeFormat);
      } else {
        return values;
      }
    } catch (error) {
      return values;
    }
  }

  if (found && found.customFieldType?.id === CustomFieldTypes.DATE && values) {
    try {
      const parsedDate = parse(values, dateInitFormats.datePickerFormat, new Date());
      if (isValid(parsedDate)) {
        return format(parsedDate, dateInitFormats.serverResponseDateFormat);
      } else {
        return values;
      }
    } catch (error) {
      return values;
    }
  }

  if (found && found.customFieldType?.id === CustomFieldTypes.TIME && values) {
    try {
      const parsedTime = parse(values, dateInitFormats.time, new Date());
      if (isValid(parsedTime)) {
        return format(parsedTime, dateInitFormats.timeLong);
      } else {
        return values;
      }
    } catch (error) {
      return values;
    }
  }

  if (found && found.customFieldType?.id === CustomFieldTypes.CHECKBOX && Array.isArray(values)) {
    const temp = {};
    found?.predefinedValues?.map((field) => {
      temp[field?.id] = values.some((el) => el?.id === field?.id);
    });
    return temp;
  }

  if (typeof values === 'object') {
    return values?.id;
  }

  return values;
};

const formatParticipantsForBooking = (participant, index, event, selectedSessions) => {
  const phoneTemp = [];
  if (participant?.contactInformation?.phone) {
    phoneTemp.push({ phone: participant?.contactInformation?.phone, typeId: 2 });
  }
  if (participant?.contactInformation?.mobile) {
    phoneTemp.push({ phone: participant?.contactInformation?.mobile, typeId: 3 });
  }
  const emailTemp = [{ email: participant?.contactInformation?.email, typeId: 2 }];

  const addressTemp = [formatDataToAddressDto(participant)];

  if (index === 0 && event?.eventParticipant) {
    event?.eventParticipant?.phoneDtos?.forEach((phone) => {
      if (![PhoneTypes.HOME, PhoneTypes.WORK].includes(phone?.type?.id)) {
        phoneTemp.push({ id: phone?.id });
      }
    });
    event?.eventParticipant?.emailDtos?.forEach((email) => {
      if (email?.type?.id !== EmailTypes.WORK) {
        emailTemp.push({ id: email?.id });
      }
    });
    event?.eventParticipant?.addressDtos?.forEach((address) => {
      if (address?.type?.id !== AddressTypes.HOME) {
        addressTemp.push({ id: address?.id });
      }
    });
  }

  const tempParticipant = {
    eventId: event?.id,
    isEventParticipant: true,
    statusId: 6,
    attendeeDto: {
      masterEventParticipantId: null,
      isLead: false,
      leadName: null,
      leadStatus: null,
      attended: false,
      hasIliasAccount: participant?.hasIliasAccount,
      iliasUsername: participant?.iliasUsername
    },
    typeId: 1,
    resultId: 12,
    companyName: participant?.generalInformation?.company || '',
    department: participant?.generalInformation?.department || '',
    customerReference: participant?.generalInformation?.reference || '',
    function: participant?.generalInformation?.functionField || '',
    firstName: participant?.generalInformation?.firstName || '',
    lastName: participant?.generalInformation?.lastName || '',
    birthDate: formatStringDateForRequest(participant?.generalInformation?.birthDate) || null,
    birthPlace: participant?.generalInformation?.birthPlace || '',
    titleId: parseInt(participant?.generalInformation?.title) || null,
    academicTitleId: parseInt(participant?.generalInformation?.academicTitle) || null,
    genderId: participant?.generalInformation?.gender || null,
    nativeLanguageId: participant?.generalInformation?.language || null,
    hotelRequest: participant?.additionalInfo?.hotelRequest || false,
    arrivalDate: participant?.additionalInfo?.hotelRequest
      ? formatNonISODateToYearMonthDay(participant?.additionalInfo?.arrivalDate)
      : null,
    departureDate: participant?.additionalInfo?.hotelRequest
      ? formatNonISODateToYearMonthDay(participant?.additionalInfo?.departureDate)
      : null,
    isVegetarian: participant?.additionalInfo?.vegetarian,
    isVegan: participant?.additionalInfo?.vegan,
    isHandicap: participant?.additionalInfo?.handicapped,
    allergies: participant?.additionalInfo?.allergies,
    addressDtos: addressTemp,
    emailDtos: emailTemp,
    phoneDtos: phoneTemp,
    discountIds: parseInt(participant?.discounts?.eventDiscount)
      ? [participant?.discounts?.eventDiscount]
      : [],
    sessionParticipants: selectedSessions
      ?.filter((session) => session?.optionalSession)
      ?.map((session) => ({
        discountIds: participant?.discounts?.optionalSessionsDiscounts
          ?.filter((item) => parseInt(item?.value) && item?.sessionId === session?.id)
          ?.map((item) => parseInt(item?.value)),
        sessionId: session?.id
      })),
    useExistingDataAsCommunication:
      participant?.communicationAddressRadio === ADDRESS_OPTIONS.noAlternative,
    communicationData:
      participant?.communicationAddressRadio === ADDRESS_OPTIONS.alternative
        ? formatBookingPerson(participant?.communicationAddress, false)
        : null,
    customFieldValueRequestDtos: participant?.additionalInfo?.customFields
      ? Object.keys(participant?.additionalInfo?.customFields).map((field) => ({
          customFieldId: parseInt(field, 10) || field,
          valueEntityId:
            index === 0 && event?.eventParticipant?.id ? event?.eventParticipant?.id || 0 : 0,
          jsonValue: formatCustomFieldValueForSave(
            participant?.additionalInfo?.customFields?.[field],
            field,
            event?.customFields
          )
        }))
      : []
  };

  return index === 0 && event?.eventParticipant
    ? omit(
        { ...tempParticipant, id: event?.eventParticipant?.id },
        'useExistingDataAsCommunication'
      )
    : tempParticipant;
};

const successStatuses = [
  ParticipantStatus.RegistrationPending,
  ParticipantStatus.RegistrationConfirmed,
  ParticipantStatus.ApprovalNeeded
];

const OrderSummaryDialog = ({
  eventData,
  steps,
  isCheckoutFormValid,
  activeStep,
  contextData,
  participants,
  contactsInfo,
  selectedSessions,
  availableSeats,
  goToDetails,
  checkedConsents = {},
  isEventFree = false
}) => {
  const [open, setOpen] = useState(false);
  const [responseContent, setResponseContent] = useState({ success: null, error: null });
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const participantWithoutBookingPerson = participants.filter(
    (participant) => !participant.isBookingPerson
  );
  const eventName = eventData?.name;

  const handleClickOpen = async () => {
    setBookingInProgress(true);
    setOpen(true);

    await postByPathAndData({
      path: BOOKING_PROCESS.POST,
      data: formatBookingProcess(
        contactsInfo,
        eventData,
        participantWithoutBookingPerson,
        selectedSessions,
        checkedConsents,
        isEventFree
      ),
      temApi: true
    })
      .then(({ data, response }) => {
        if (!data) {
          const serverMessage = response?.data?.message;
          setResponseContent({ success: null, error: serverMessage });
          return;
        }
        const temp = [];
        data?.bookedParticipants?.forEach((participant) => {
          temp.push({
            participantId: participant?.attendee?.uuid,
            name: `${participant?.attendee?.firstName} ${participant?.attendee?.lastName}`,
            status: selectedSessions?.map((session) => {
              if (!session?.optionalSession) {
                return true;
              } else {
                return successStatuses.includes(
                  participant?.sessionParticipants?.find(
                    (item) => item?.session?.id === session?.id
                  )?.status?.id
                );
              }
            })
          });
        });
        temp.reverse();
        data?.participantsOnWaitingList?.forEach((participant) => {
          temp.push({
            participantId: participant?.attendee?.uuid,
            name: `${participant?.attendee?.firstName} ${participant?.attendee?.lastName}`,
            status: Array(selectedSessions?.length).fill(false)
          });
        });
        setResponseContent({
          success: { participantsStatuses: temp, bookingNumber: data?.bookingNumber },
          error: null
        });
      })
      .catch((error) => {
        const serverMessage = error?.response?.data?.message || error?.message;
        setResponseContent({ success: null, error: serverMessage });
      })
      .finally(() => {
        setBookingInProgress(false);
      });
  };

  const handleClose = (_, reason) => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };

  return (
    <Fragment>
      <StyledButton
        data-test-id='book-event-button'
        variant='contained'
        onClick={handleClickOpen}
        disabled={!isCheckoutFormValid || bookingInProgress}
        lastStep={steps?.length != activeStep + 1}
      >
        {isEventFree ? Localize.get('Booking.bookEventFree') : Localize.get('Booking.bookEvent')}
      </StyledButton>

      <StyledDialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        fullWidth={true}
        maxWidth='md'
      >
        <StyledDialogTitle id='alert-dialog-title'>
          <ComponentHeader eventData={eventData} availableSeats={availableSeats?.count || 0} />
        </StyledDialogTitle>

        <DialogContent sx={{ pt: 0, pb: 4, fontSize: '1rem', minHeight: '10rem' }}>
          {bookingInProgress ? (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '35vh',
                color: 'primary.main'
              }}
            >
              <CircularProgress color='inherit' size={50} />
            </Box>
          ) : (
            <>
              {responseContent?.error && (
                <>
                  <Alert
                    sx={{ width: 'fit-content', fontSize: '1rem', mt: 3, mb: 3 }}
                    severity='error'
                  >
                    {`${Localize.get('Booking.registrationFailed')} ${Localize.get('Booking.details')}: ${responseContent.error}`}
                  </Alert>
                </>
              )}
              {responseContent?.success && (
                <>
                  <Typography sx={{ mb: 2, textAlign: { xs: 'center', sm: 'left' } }} variant='h5'>
                    {Localize.get('Booking.confirmation')}
                  </Typography>
                  <StyledAlert icon={false} severity='success'>
                    {Localize.get('Booking.confirmationEmailFirst', {
                      eventName: `"${eventName}"`
                    })}
                  </StyledAlert>
                  <Typography sx={{ fontWeight: 'bold', mb: 2 }}>
                    {`${Localize.get('Booking.bookingNumber')} ${responseContent?.success?.bookingNumber}`}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>
                    {Localize.get('Booking.confirmationEmailSecond')}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>
                    {Localize.get('Booking.confirmationEmailThird')}
                  </Typography>

                  {selectedSessions?.length ? (
                    <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                        <TableHead>
                          <TableRow key={Localize.get('Booking.participant')}>
                            <StyledTableCell key={Localize.get('Booking.participant')}>
                              {Localize.get('Booking.participant')}
                            </StyledTableCell>
                            {selectedSessions?.map((session) => {
                              return (
                                <StyledTableCell key={session?.id} align='left'>
                                  {`${session?.name} (${
                                    !session?.optionalSession
                                      ? Localize.get('Booking.mandatory')
                                      : Localize.get('Booking.optional')
                                  })`}
                                </StyledTableCell>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {responseContent.success?.participantsStatuses?.map(
                            (row, participantIndex) => {
                              return (
                                <TableRow
                                  key={row?.participantId}
                                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                  <TableCell
                                    key={participantIndex}
                                    component='th'
                                    scope='row'
                                    sx={{ fontSize: '1rem' }}
                                  >
                                    {row?.name}
                                  </TableCell>
                                  {row?.status?.map((status, index) => {
                                    return (
                                      <TableCell key={index} align='left'>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                            gap: 1
                                          }}
                                        >
                                          {status ? (
                                            <>
                                              <CheckIcon color='success' />
                                              <Typography>
                                                {Localize.get('Booking.booked')}
                                              </Typography>
                                            </>
                                          ) : (
                                            <>
                                              <HourglassBottomIcon color='warning' />
                                              <Typography>
                                                {Localize.get('CustomerLoyaltyEvents.waitingList')}
                                              </Typography>
                                            </>
                                          )}
                                        </Box>
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              );
                            }
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Paper sx={{ p: 2, mt: 2, display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ display: 'inline', fontWeight: 'bold' }}>
                        {`${
                          responseContent?.success?.participantsStatuses?.length > 1
                            ? Localize.get('Booking.participants')
                            : Localize.get('Booking.participant')
                        }: `}
                      </Typography>
                      <Typography sx={{ display: 'inline', ml: 1 }}>
                        {responseContent?.success?.participantsStatuses
                          ?.map((participant) => participant?.name)
                          .join(', ')}
                      </Typography>
                    </Paper>
                  )}

                  <Typography mt={2}>{Localize.get('Booking.anotherConfirmationEmail')}</Typography>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: (theme) => theme.palette.booking.dialogFooter,
            justifyContent: { xs: 'center', md: 'flex-end' }
          }}
        >
          <StyledLink
            to={goToDetails}
            state={{
              from: contextData?.user
                ? Localize.get('AppNavBar.catalog')
                : Localize.get('AppNavBar.eventCatalog'),
              path: routerConstants.routes.name.catalog
            }}
          >
            {Localize.get('Booking.goToDetails')}
          </StyledLink>

          {responseContent?.success && contextData?.isCatalogVisible && (
            <StyledLink
              to={{
                pathname: routerConstants.routes.name.catalog,
                state: { from: 'eventBooking', path: routerConstants.routes.name.eventBooking }
              }}
            >
              {Localize.get('Booking.goToCatalog')}
            </StyledLink>
          )}
        </DialogActions>
      </StyledDialog>
    </Fragment>
  );
};

export default OrderSummaryDialog;
